import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Typography
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { createPattern, deletePattern, getAllPatterns } from '../../features/green/greenSlice';
import { useTitle } from '../../hooks/titleProvider';
import { hasWorkspaceAccess, Roles } from '../../helpers/access';
import { useAuth } from '../../contexts/authContext';
import { getVenues } from '../../features/venue/venueSlice';
import GreenPatternBuilderDialog from './greenPatternBuilderDialog';
import {
  DEFAULT_GREENS,
  DEFAULT_RINKS_PER_GREEN,
  clampPositiveInt,
  normalizeVenueToLocation
} from './greenPatternLayout';

const resolveHomeClubId = (profile) => {
  const firstClubEntry =
    profile?.clubs && Array.isArray(profile.clubs) ? profile.clubs[0] || {} : {};

  const candidate =
    profile?.homeClub ||
    profile?.homeClubId ||
    profile?.clubId ||
    firstClubEntry?.club ||
    firstClubEntry?.clubId ||
    firstClubEntry?.venueId ||
    firstClubEntry?.venue ||
    firstClubEntry?.id ||
    '';

  return String(candidate || '').trim();
};

const PATTERN_EXPORT_FIELDS = [
  'name',
  'sectionsCount',
  'roundsCount',
  'gamesPerRound',
  'greensCount',
  'rinksCount',
  'locationsCount',
  'greenDirectionsByRound',
  'pattern',
  'venueId',
  'venueName',
  'venueLayout',
  'fallbackGreensCount',
  'fallbackRinksPerGreen'
];

const patternToExportData = (pattern) => {
  const data = {};
  PATTERN_EXPORT_FIELDS.forEach((field) => {
    if (pattern && pattern[field] !== undefined) {
      data[field] = pattern[field];
    }
  });
  return data;
};

const GreenPatternManager = () => {
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const { currentUserProfile, currentWorkspace } = useAuth();
  const patterns = useSelector((state) => state.green.patterns || []);
  const loadingPatterns = useSelector((state) => state.green.isLoading);
  const venues = useSelector((state) => state.venue.venues || []);
  const loadingVenues = useSelector((state) => state.venue.loadingVenues);

  const [selectedVenueIds, setSelectedVenueIds] = useState([]);
  const [fallbackGreensCount, setFallbackGreensCount] = useState(DEFAULT_GREENS);
  const [fallbackRinksPerGreen, setFallbackRinksPerGreen] = useState(DEFAULT_RINKS_PER_GREEN);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [builderPatternId, setBuilderPatternId] = useState('');
  const [lockVenueForDialog, setLockVenueForDialog] = useState(false);
  const [activePatternMeta, setActivePatternMeta] = useState(null);
  const [deleteTargetPattern, setDeleteTargetPattern] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importError, setImportError] = useState('');

  const canManage = hasWorkspaceAccess(currentUserProfile, currentWorkspace, Roles.ADMIN);

  useEffect(() => {
    setTitle('Green Patterns');
    dispatch(getAllPatterns());
    dispatch(getVenues());
  }, [dispatch, setTitle]);

  useEffect(() => {
    if (selectedVenueIds.length === 0 && venues.length > 0) {
      const homeClubId = resolveHomeClubId(currentUserProfile);
      const matchedHomeVenue = venues.find((venue) => String(venue?.id) === homeClubId);
      setSelectedVenueIds([matchedHomeVenue?.id || venues[0].id]);
    }
  }, [selectedVenueIds, venues, currentUserProfile]);

  const sortedPatterns = useMemo(() => {
    return [...patterns].sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || '')));
  }, [patterns]);

  const selectedVenues = useMemo(() => {
    if (!Array.isArray(selectedVenueIds) || selectedVenueIds.length === 0) {
      return [];
    }

    const selectedIds = new Set(selectedVenueIds.map((id) => String(id)));
    return venues.filter((venue) => selectedIds.has(String(venue?.id)));
  }, [venues, selectedVenueIds]);

  const allSelectedVenuesHaveGreens =
    selectedVenues.length > 0 &&
    selectedVenues.every((venue) => Array.isArray(venue?.greens) && venue.greens.length > 0);

  const builderLocations = useMemo(() => {
    if (
      lockVenueForDialog &&
      Array.isArray(activePatternMeta?.venueLayout) &&
      activePatternMeta.venueLayout.length > 0 &&
      selectedVenues.length === 0
    ) {
      return [
        {
          id: String(activePatternMeta.venueId || 'archived-club'),
          name: String(activePatternMeta.venueName || 'Archived Club'),
          greens: activePatternMeta.venueLayout
        }
      ];
    }

    if (selectedVenues.length === 0) {
      return [
        normalizeVenueToLocation(
          { id: 'default-club', name: 'Default Club', greens: [] },
          fallbackGreensCount,
          fallbackRinksPerGreen
        )
      ];
    }

    return selectedVenues.map((venue) =>
      normalizeVenueToLocation(venue, fallbackGreensCount, fallbackRinksPerGreen)
    );
  }, [
    activePatternMeta,
    lockVenueForDialog,
    selectedVenues,
    fallbackGreensCount,
    fallbackRinksPerGreen
  ]);

  const openBuilder = (patternId = '') => {
    if (patternId) {
      const patternToOpen = patterns.find((item) => item.id === patternId) || null;
      const firstPatternLocationId =
        Array.isArray(patternToOpen?.pattern) && patternToOpen.pattern.length > 0
          ? patternToOpen.pattern.find((entry) => Boolean(entry?.locationId))?.locationId || ''
          : '';
      const entryVenueIds = Array.isArray(patternToOpen?.pattern)
        ? Array.from(
            new Set(
              patternToOpen.pattern.map((entry) => String(entry?.locationId || '').trim()).filter(Boolean)
            )
          )
        : [];
      const storedVenueIds = Array.isArray(patternToOpen?.venueIds)
        ? patternToOpen.venueIds.map((id) => String(id || '').trim()).filter(Boolean)
        : [];
      const nextVenueIds =
        storedVenueIds.length > 0
          ? storedVenueIds
          : entryVenueIds.length > 0
            ? entryVenueIds
            : String(patternToOpen?.venueId || firstPatternLocationId || '').trim()
              ? [String(patternToOpen?.venueId || firstPatternLocationId || '').trim()]
              : [];

      if (nextVenueIds.length > 0) {
        setSelectedVenueIds(nextVenueIds);
      }

      if (Number(patternToOpen?.fallbackGreensCount) > 0) {
        setFallbackGreensCount(Number(patternToOpen.fallbackGreensCount));
      } else if (Number(patternToOpen?.greensCount) > 0) {
        setFallbackGreensCount(Number(patternToOpen.greensCount));
      }

      if (Number(patternToOpen?.fallbackRinksPerGreen) > 0) {
        setFallbackRinksPerGreen(Number(patternToOpen.fallbackRinksPerGreen));
      } else if (Number(patternToOpen?.greensCount) > 0 && Number(patternToOpen?.rinksCount) > 0) {
        setFallbackRinksPerGreen(
          Math.max(1, Math.round(Number(patternToOpen.rinksCount) / Number(patternToOpen.greensCount)))
        );
      }

      setActivePatternMeta(patternToOpen);
      setLockVenueForDialog(true);
      setBuilderPatternId(patternId);
      setBuilderOpen(true);
      return;
    }

    setActivePatternMeta(null);
    setLockVenueForDialog(false);
    if (venues.length > 0) {
      const homeClubId = resolveHomeClubId(currentUserProfile);
      const matchedHomeVenue = venues.find((venue) => String(venue?.id) === homeClubId);
      setSelectedVenueIds([matchedHomeVenue?.id || venues[0].id]);
    }
    setBuilderPatternId(patternId);
    setBuilderOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTargetPattern?.id) {
      return;
    }

    await dispatch(deletePattern(deleteTargetPattern.id));
    setDeleteTargetPattern(null);
  };

  const buildExportJson = (pattern) => JSON.stringify(patternToExportData(pattern), null, 2);

  const handleDownloadExport = (pattern) => {
    if (!pattern) return;
    const json = buildExportJson(pattern);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const safeName =
      String(pattern.name || 'green-pattern')
        .replace(/[^a-z0-9-_ ]/gi, '')
        .trim() || 'green-pattern';
    link.href = url;
    link.download = `${safeName}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleImportFile = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;

    setImportError('');

    let text;
    try {
      text = await file.text();
    } catch (error) {
      setImportError(`Could not read file: ${error?.message || error}`);
      return;
    }

    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch {
      setImportError('Invalid pattern file: not valid JSON');
      return;
    }

    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      setImportError('Invalid pattern file: expected a JSON object');
      return;
    }

    const payload = { name: parsed.name || 'Imported Pattern' };
    PATTERN_EXPORT_FIELDS.forEach((field) => {
      if (parsed[field] !== undefined) {
        payload[field] = parsed[field];
      }
    });

    try {
      await dispatch(createPattern(payload));
      await dispatch(getAllPatterns());
      setImportOpen(false);
      setImportError('');
    } catch {
      // createPattern thunk already surfaces its own error via its alert
      setImportError('Failed to import pattern');
    }
  };

  return (
    <Stack spacing={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
        <Typography variant="h4">Green Patterns</Typography>
        {canManage ? (
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={() => { setImportError(''); setImportOpen(true); }}>
              Import
            </Button>
            <Button variant="contained" onClick={() => openBuilder()}>
              Create Pattern
            </Button>
          </Stack>
        ) : null}
      </Box>

      {!canManage ? (
        <Typography variant="body2" color="text.secondary">
          View-only mode. Ask an admin to create or delete patterns.
        </Typography>
      ) : null}

      <Grid container spacing={2}>
        {sortedPatterns.map((pattern) => (
          <Grid item xs={12} md={6} lg={4} key={pattern.id}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6">{pattern.name || 'Unnamed Pattern'}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Sections: {pattern.sectionsCount || 0} | Rounds: {pattern.roundsCount || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Games/Section: {pattern.gamesPerRound || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Locations: {pattern.locationsCount || 0} | Greens: {pattern.greensCount || 0} |
                  Rinks: {pattern.rinksCount || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Venue: {pattern.venueName || 'Not set'}
                </Typography>
              </CardContent>
              <CardActions>
                <Button size="small" onClick={() => handleDownloadExport(pattern)}>
                  Export
                </Button>
                <Button size="small" onClick={() => openBuilder(pattern.id)}>
                  Open
                </Button>
                {canManage ? (
                  <Button size="small" color="error" onClick={() => setDeleteTargetPattern(pattern)}>
                    Delete
                  </Button>
                ) : null}
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {sortedPatterns.length === 0 && !loadingPatterns ? (
        <Typography variant="body2" color="text.secondary">
          No patterns found.
        </Typography>
      ) : null}

      <GreenPatternBuilderDialog
        open={builderOpen}
        onClose={() => {
          setBuilderOpen(false);
          setBuilderPatternId('');
          setLockVenueForDialog(false);
          setActivePatternMeta(null);
        }}
        event={{
          id: 'green-pattern-manager',
          teams: [],
          hasSections: true,
          gameFormat: { rounds: 1, gamesPerRound: 1 }
        }}
        sections={Array.from({ length: 3 }, (_, idx) => ({ id: `s-${idx + 1}` }))}
        rounds={1}
        gamesPerRound={1}
        greens={builderLocations}
        venueOptions={venues}
        selectedVenueIds={selectedVenueIds}
        onSelectedVenueIdsChange={setSelectedVenueIds}
        fallbackGreensCount={clampPositiveInt(fallbackGreensCount, DEFAULT_GREENS)}
        fallbackRinksPerGreen={clampPositiveInt(fallbackRinksPerGreen, DEFAULT_RINKS_PER_GREEN)}
        onFallbackGreensCountChange={setFallbackGreensCount}
        onFallbackRinksPerGreenChange={setFallbackRinksPerGreen}
        lockVenue={lockVenueForDialog}
        hasVenueGreens={allSelectedVenuesHaveGreens}
        initialPatternId={builderPatternId}
        readOnly={!canManage}
        onSave={async () => {
          await dispatch(getAllPatterns());
          setBuilderOpen(false);
          setBuilderPatternId('');
        }}
      />

      {loadingVenues && venues.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Loading clubs...
        </Typography>
      ) : null}

      <Dialog
        open={Boolean(deleteTargetPattern)}
        onClose={() => setDeleteTargetPattern(null)}
        maxWidth="xs"
        fullWidth>
        <DialogTitle>Delete Pattern</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            Are you sure you want to delete "{deleteTargetPattern?.name || 'this pattern'}"?
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTargetPattern(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        maxWidth="xs"
        fullWidth>
        <DialogTitle>Import Pattern</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a .json pattern file to import.
          </Typography>
          <Button variant="contained" component="label">
            Choose File
            <input type="file" hidden accept=".json,application/json" onChange={handleImportFile} />
          </Button>
          {importError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {importError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default GreenPatternManager;
